import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { listAccountCategoriesForSummary, getCategorySummary } from "../../api/accountApi";
import "../Attendance/Attendance.css";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + "01";

/**
 * Direct port of digicampusbd's `general-accounts-report-category-list-summary`
 * ("Financial Category Statement") — ledger summary for one or more chosen
 * chart-of-accounts categories (Income/Expense/Asset/Liability), optionally
 * filtered to chosen users, same per-row debit/credit net-off logic as the
 * Income Statement. New `GET /api/accounts/vouchers/reports/category-summary`
 * mirrors `GeneralAccountReportService.ledgerSummaryByCategoryId`'s two native
 * queries (with/without a user filter) verbatim.
 */
export default function CategorySummaryPage() {
  const [categories, setCategories] = useState([]);
  const [categoryIds, setCategoryIds] = useState([]);
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    listAccountCategoriesForSummary().then((list) => {
      setCategories(list);
      setCategoryIds(list.map((c) => String(c.categoryId)));
    });
  }, []);

  const toggleCategory = (id) => setCategoryIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSearch = async () => {
    setError("");
    if (categoryIds.length === 0) {
      setError("Please select at least one category.");
      return;
    }
    setLoading(true);
    try {
      setData(await getCategorySummary(categoryIds, [], fromDate, toDate));
    } catch (err) {
      setError("Failed to load report.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Financial Category Statement</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      <div className="form-row">
        <span className="admit-card-label">Categories:</span>{" "}
        {categories.map((c) => (
          <label key={c.categoryId} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
            <input
              type="checkbox"
              checked={categoryIds.includes(String(c.categoryId))}
              onChange={() => toggleCategory(String(c.categoryId))}
            />
            {c.categoryName}
          </label>
        ))}
      </div>
      <div className="attendance-filters">
        <label>
          From Date
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label>
          To Date
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        data && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Ledger</th>
                  <th>Category</th>
                  <th>User</th>
                  <th>Debit</th>
                  <th>Credit</th>
                </tr>
              </thead>
              <tbody>
                {data.ledgerSummaryList.map((r, i) => (
                  <tr key={i}>
                    <td>{r.ledgerName}</td>
                    <td>{r.categoryName}</td>
                    <td>{r.userName}</td>
                    <td>{r.debitAmount.toFixed(2)}</td>
                    <td>{r.creditAmount.toFixed(2)}</td>
                  </tr>
                ))}
                {data.ledgerSummaryList.length === 0 && (
                  <tr>
                    <td colSpan={5}>No transactions found for this selection.</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} style={{ fontWeight: 800, textAlign: "right" }}>
                    Grand Total
                  </td>
                  <td>
                    <strong>{data.totalDebit.toFixed(2)}</strong>
                  </td>
                  <td>
                    <strong>{data.totalCredit.toFixed(2)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}
    </MainLayout>
  );
}
